import { motion } from 'framer-motion'
import { Badge, Card } from './ui'
import { L1Demo } from './L1Demo'
import { L2Demo } from './L2Demo'
import { L3Demo } from './L3Demo'
import type { LayerStanza } from '../types'
import { useOrion } from '../lib/state'
import { useT } from '../lib/i18n'

interface LayerSectionProps {
  layer: LayerStanza
}

export function LayerSection({ layer }: LayerSectionProps) {
  const { setActiveLayer } = useOrion()
  const t = useT()
  const s = t.layerSection
  const isOntology = layer.id === 'L3'

  return (
    <motion.section
      id={layer.id}
      onViewportEnter={() => setActiveLayer(layer.id)}
      viewport={{ amount: 0.35, once: false }}
      className="py-20 sm:py-28 scroll-mt-32"
    >
      <div className="flex items-baseline gap-4 mb-2">
        <span
          className={`mono text-[length:var(--text-small)] tracking-widest ${
            isOntology ? 'text-[color:var(--color-orion-amber)]' : 'text-[color:var(--color-orion-haze)]'
          }`}
        >
          {layer.shortLabel}
        </span>
        {isOntology && <Badge tone="warn">{s.highlightBadge}</Badge>}
      </div>

      <motion.h2
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        style={{ fontSize: 'var(--text-h1)' }}
        className={`mb-4 ${isOntology ? 'text-[color:var(--color-orion-amber)]' : 'text-[color:var(--color-orion-text)]'}`}
      >
        {layer.techName}
      </motion.h2>

      <p
        className="text-[color:var(--color-orion-haze)] max-w-3xl mb-8"
        style={{ fontSize: 'var(--text-lead)' }}
      >
        {layer.oneLiner}
      </p>

      <div className="mb-8">
        {layer.id === 'L1' && <L1Demo />}
        {layer.id === 'L2' && <L2Demo />}
        {layer.id === 'L3' && <L3Demo />}
      </div>

      {layer.fatalGap ? (
        <div className="flex items-start gap-3 pl-4 border-l-2 border-[color:var(--color-orion-block)]/50">
          <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-block)] shrink-0 pt-0.5">
            {s.fatalGapLabel}
          </span>
          <p className="text-[color:var(--color-orion-haze)]" style={{ fontSize: 'var(--text-body)' }}>
            {layer.fatalGap}
          </p>
        </div>
      ) : (
        <Card glow="amber" className="border-[color:var(--color-orion-amber)]/40">
          <div className="flex items-start gap-3">
            <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-amber)] shrink-0 pt-0.5">
              {s.pivotLabel}
            </span>
            <p className="text-[color:var(--color-orion-text)]" style={{ fontSize: 'var(--text-lead)', lineHeight: 1.55 }}>
              {s.pivotBody1}
              <span className="text-[color:var(--color-orion-amber)] font-medium">{s.pivotSemanticSpan}</span>
              {s.pivotBody2}
              <strong className="text-[color:var(--color-orion-amber)] font-semibold">{s.pivotPathSpan}</strong>
              {s.pivotBody3}
              <span className="text-[color:var(--color-orion-amber)] font-medium">{s.pivotFourQualitiesSpan}</span>
              {s.pivotBody4}
            </p>
          </div>
        </Card>
      )}
    </motion.section>
  )
}
